import React, { useState, useRef } from "react";
import {
  Button,
  Container,
  Grid,
  Paper,
  TextField,
  InputAdornment,
  IconButton,
  Link,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import ReCAPTCHA from "react-google-recaptcha";
import api from "../../api/axios";
import { useNavigate, useLocation } from "react-router-dom";
import useAuth from "../../hooks/useAuth";
import { jwtDecode } from "jwt-decode";

const SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

export default function Login() {
  const { setAuth } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || "/";
  const [showPass, setShowPass] = useState(false);
  const [captchaValue, setCaptchaValue] = useState(null);
  const [require2fa, setRequire2fa] = useState(false);
  const recaptchaRef = useRef(null);

  const handleCaptchaChange = (value) => {
    setCaptchaValue(value);
  };

  const handlePassVisibility = () => {
    setShowPass(!showPass);
  };

  const onSubmit = async (data, props) => {
    if (!captchaValue) {
      toast.error("Por favor, verifique el captcha");
      return;
    }
    try {
      const response = await api.post("/api/v1/login", {
        user: {
          email: data.email,
          password: data.password,
          otp_attempt: data.otp_attempt,
        },
        captcha: captchaValue,
      });

      

      const accessToken = response?.data?.token;
      const userName = response?.data?.user.name;
      const decoded = jwtDecode(accessToken);
      const role = decoded.role;

      localStorage.setItem("accessToken", accessToken);
      localStorage.setItem("userName", userName);
      localStorage.setItem("role", role);
      setAuth({ accessToken, role, userName });
      navigate(from, { replace: true });
      toast.success(response.data.message);
      setRequire2fa(false);
    } catch (err) {
      if (!err?.response) {
        toast.error("No responde el servidor");
      } else {
        if (err.response.data.require_2fa) {
          recaptchaRef.current.reset();
          setCaptchaValue(null);
          toast.info(
            "Ingrese su código de autenticación 2FA y complete el CAPTCHA nuevamente"
          );
          setRequire2fa(true);
        } else {
          toast.error(err.response.data.error);
        }
      }
    }
  };

  const initialValues = {
    email: "",
    password: "",
    otp_attempt: "",
  };

  const validationSchema = Yup.object().shape({
    email: Yup.string()
      .required("Ingrese su correo")
      .email("Debe ser un correo válido")
      .min(5, "No puede tener menos de 5 caracteres")
      .max(254, "No puede tener más de 254 caracteres"),
    password: Yup.string()
      .min(12, "No puede tener menos de 12 caracteres")
      .required("Ingrese su contraseña")
      .max(128, "No puede tener más de 128 caracteres"),
    otp_attempt: require2fa
      ? Yup.string()
          .length(6, "El código debe tener 6 dígitos")
          .required("Ingrese el código 2FA")
      : Yup.string().notRequired(),
  });

  return (
    <Container maxWidth="sm">
      <h1 align="center">Gestión y seguimiento de convenios</h1>
      <h1 align="center">Acceso a la plataforma</h1>
      <Grid container spacing={2} direction="column" justifyContent="center">
        <Paper elevation={2} sx={{ padding: 5 }}>
          <Formik
            initialValues={initialValues}
            onSubmit={onSubmit}
            validationSchema={validationSchema}
            validateOnBlur={false}
          >
            {(props) => (
              <Form>
                <Grid container direction="column" spacing={2}>
                  <Grid item>
                    <Field
                      as={TextField}
                      name="email"
                      type="text"
                      fullWidth
                      label="Ingrese su correo"
                      placeholder="Correo electrónico"
                      variant="outlined"
                      autoFocus
                      error={!!props.errors.email && props.touched.email}
                      helperText={<ErrorMessage name="email" />}
                      disabled={require2fa}
                    />
                  </Grid>
                  <Grid item>
                    <Field
                      as={TextField}
                      name="password"
                      type={showPass ? "text" : "password"}
                      fullWidth
                      label="Ingrese su contraseña"
                      placeholder="Contraseña"
                      variant="outlined"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={handlePassVisibility}
                              aria-label="toggle password"
                              edge="end"
                            >
                              {showPass ? (
                                <VisibilityIcon />
                              ) : (
                                <VisibilityOffIcon />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      error={!!props.errors.password && props.touched.password}
                      helperText={<ErrorMessage name="password" />}
                      disabled={require2fa}
                    />
                  </Grid>

                  <Grid item>
                    <ReCAPTCHA
                      ref={recaptchaRef}
                      sitekey={SITE_KEY}
                      onChange={handleCaptchaChange}
                      onExpired={() => {
                        setCaptchaValue(null);
                        toast.warn(
                          "Captcha expirado, por favor complételo de nuevo"
                        );
                      }}
                    />
                  </Grid>

                  {require2fa && (
                    <Grid item>
                      <Field
                        as={TextField}
                        name="otp_attempt"
                        label="Código de autenticación"
                        placeholder="123456"
                        fullWidth
                        variant="outlined"
                        error={
                          !!props.errors.otp_attempt &&
                          props.touched.otp_attempt
                        }
                        helperText={<ErrorMessage name="otp_attempt" />}
                      />
                    </Grid>
                  )}

                  <Grid item>
                    <Button
                      variant="contained"
                      fullWidth
                      type="submit"
                      disabled={props.isSubmitting}
                    >
                      {require2fa ? "Verificar código 2FA" : "Ingresar"}
                    </Button>
                  </Grid>

                  <Grid item>
                    <Link
                      component="button"
                      variant="body2"
                      onClick={() => navigate("/forgot-password")}
                    >
                      ¿Olvidaste tu contraseña?
                    </Link>
                  </Grid>
                  <Grid item>
                    <Link
                      component="button"
                      variant="body2"
                      onClick={() => navigate("/resend-confirmation")}
                    >
                      Re-enviar confirmación de correo
                    </Link>
                  </Grid>
                </Grid>
              </Form>
            )}
          </Formik>
        </Paper>
      </Grid>
    </Container>
  );
}
